import React, { useState, useEffect } from "react";
import {
  ReferenceList,
  ReferenceValueView,
  ReferenceValueSynonymView
} from "@/entities/all";
import {
  ArrowLeft,
  PlusCircle,
  List,
  Users,
  Trash2,
  Pencil
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Skeleton } from "@/components/ui/skeleton";

import ReferenceValuesTable from "../components/references/ReferenceValuesTable";
import CreateValueModal from "../components/references/CreateValueModal";
import DeleteValueDialog from "../components/references/DeleteValueDialog";
import PaginatedTable from "../components/ui/paginated-table";
import EditReferenceListModal from "../components/references/EditReferenceListModal";
import DeleteReferenceListDialog from "../components/references/DeleteReferenceListDialog";
import ReferenceListDependencies from "../components/references/ReferenceListDependencies";
import ReferenceValueLinks from "../components/references/ReferenceValueLinks";

function CounterPill({ icon: Icon, count, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[14px] bg-card-bg border border-card-border text-sm font-medium text-page-text">
      <Icon size={14} className="text-nav-active" />
      {count} {label}
    </span>
  );
}

export default function ReferenceListValues() {
  const navigate = useNavigate();
  const [referenceList, setReferenceList] = useState(null);
  const [values, setValues] = useState([]);
  const [synonyms, setSynonyms] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [isEditListModalOpen, setIsEditListModalOpen] = useState(false);
  const [isDeleteListDialogOpen, setIsDeleteListDialogOpen] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const listId = params.get('id');
  const depId = params.get('dep');

  useEffect(() => {
    if (listId) {
      loadData();
    }
  }, [listId]);

  useEffect(() => {
    filterValues();
  }, [values, synonyms, searchTerm]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [listData, valuesData, synonymsData] = await Promise.all([
        ReferenceList.filter({ id: listId }).then(lists => lists[0]),
        ReferenceValueView.filter({ list_id: listId }, 'canonical_value'),
        ReferenceValueSynonymView.filter({ list_id: listId })
      ]);
      setReferenceList(listData);
      setValues(valuesData);
      setSynonyms(synonymsData);
    } catch (error) {
      console.error('Error loading reference list data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterValues = () => {
    let filtered = values;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = values.filter(value => {
        if (value.canonical_value.toLowerCase().includes(term)) return true;
        if (value.value_description?.toLowerCase().includes(term)) return true;
        const valueSynonyms = synonyms.filter(s => s.reference_value_id === value.id);
        if (valueSynonyms.some(s => s.synonym?.toLowerCase().includes(term))) return true;
        return false;
      });
    }
    setFilteredValues(filtered);
  };

  const handleValueCreated = () => {
    loadData();
    setIsCreateModalOpen(false);
  };

  const handleDeleteValue = (value) => {
    setSelectedValue(value);
    setIsDeleteDialogOpen(true);
  };

  const handleValueDeleted = () => {
    loadData();
    setIsDeleteDialogOpen(false);
    setSelectedValue(null);
  };

  const getValueSynonyms = (valueId) => {
    return synonyms.filter(synonym => synonym.reference_value_id === valueId);
  };

  const handleBack = () => navigate(createPageUrl('reference-lists'));

  if (isLoading) {
    return (
      <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-9 w-full rounded-[18px]" />
        <Skeleton className="h-[300px] w-full rounded-[8px]" />
      </div>
    );
  }

  return (
    <div className="pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Back */}
      <button
        onClick={handleBack}
        className="flex items-center gap-1 w-fit text-sm text-nav-active cursor-pointer bg-transparent border-0 p-0 hover:text-nav-active-hover transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Reference Lists
      </button>

      {/* Title + Actions */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">
            {referenceList?.list_name}
          </h1>
          {referenceList?.description && (
            <p className="text-sm font-normal text-page-muted">
              {referenceList.description}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <button
            onClick={() => setIsDeleteListDialogOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 text-sm font-medium cursor-pointer bg-card-bg hover:bg-red-500/10 transition-colors"
            style={{ borderColor: 'rgba(222,111,107,0.7)', color: 'rgba(222,111,107,0.9)' }}
          >
            <Trash2 size={20} strokeWidth={2.5} />
            Delete
          </button>
          <button
            onClick={() => setIsEditListModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <Pencil size={20} strokeWidth={2.5} />
            Edit
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            Add Value
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="flex flex-wrap items-center gap-2">
        <CounterPill icon={List} count={values.length} label={values.length === 1 ? 'Value' : 'Values'} />
        <CounterPill icon={Users} count={synonyms.length} label={synonyms.length === 1 ? 'Synonym' : 'Synonyms'} />
      </div>

      {/* Search */}
      <div className="flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
          <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="var(--nav-active)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          placeholder="Search values, descriptions, and synonyms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted min-w-0"
        />
      </div>

      {/* Dependencies */}
      <ReferenceListDependencies listId={listId} listName={referenceList?.list_name} />

      {/* Value Links (shown when dep query param is present) */}
      {depId && <ReferenceValueLinks depId={depId} listId={listId} />}

      {/* Values Table */}
      <PaginatedTable
        data={filteredValues}
        externalLoading={isLoading}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <ReferenceValuesTable
              values={paginatedData}
              synonyms={synonyms}
              isLoading={loading}
              listId={listId}
              onValueUpdate={loadData}
              getValueSynonyms={getValueSynonyms}
              onDeleteValue={handleDeleteValue}
            />
          </div>
        )}
      />

      <CreateValueModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onValueCreated={handleValueCreated}
        listId={listId}
      />

      {selectedValue && (
        <DeleteValueDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => { setIsDeleteDialogOpen(false); setSelectedValue(null); }}
          onValueDeleted={handleValueDeleted}
          value={selectedValue}
        />
      )}

      <EditReferenceListModal
        isOpen={isEditListModalOpen}
        onClose={() => setIsEditListModalOpen(false)}
        onListUpdated={() => {
          setIsEditListModalOpen(false);
          loadData();
        }}
        list={referenceList}
      />

      <DeleteReferenceListDialog
        isOpen={isDeleteListDialogOpen}
        onClose={() => setIsDeleteListDialogOpen(false)}
        onListDeleted={() => navigate(createPageUrl('reference-lists'))}
        list={referenceList}
      />
    </div>
  );
}
